import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import api from "~/api/api";
import { Circles } from "react-loader-spinner";

const AuthContext = createContext();
const apiUrl = process.env.REACT_APP_API_URL;

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchSession = async () => {
        setLoading(true);
        try {
            // Use raw axios (NOT api instance) for initial session check
            // so it won't trigger the toast interceptor on 401
            const path = window.location.pathname;
            let userRes = { status: 'rejected' };
            let adminRes = { status: 'rejected' };

            if (path.includes('/administrator') || path.includes('/admin-signin')) {
                adminRes = await axios.get(`${apiUrl}/api/auth/admin/me`, { withCredentials: true })
                    .then(res => ({ status: 'fulfilled', value: res }))
                    .catch(err => ({ status: 'rejected', reason: err }));
            } else {
                userRes = await axios.get(`${apiUrl}/api/auth/user/me`, { withCredentials: true })
                    .then(res => ({ status: 'fulfilled', value: res }))
                    .catch(err => ({ status: 'rejected', reason: err }));
            }

            let finalUser = null;
            let finalAdmin = null;

            if (userRes.status === 'fulfilled' && userRes.value.data) {
                const raw = userRes.value.data;
                const userData = raw.Data ?? raw;
                finalUser = userData.Id ? userData : null;
                setUser(finalUser);
            } else {
                setUser(null);
            }

            if (adminRes.status === 'fulfilled' && adminRes.value.data) {
                const raw = adminRes.value.data;
                const adminData = raw.Data ?? raw;
                finalAdmin = adminData.Id ? adminData : null;
                setAdmin(finalAdmin);
            } else {
                setAdmin(null);
            }

            return { user: finalUser, admin: finalAdmin };
        } catch (error) {
            setUser(null);
            setAdmin(null);
            return { user: null, admin: null };
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSession();
        // Reset logout flag on mount
        window.isLoggingOut = false;

        const handleSilentLogout = () => {
            setUser(null);
            setAdmin(null);
        };
        window.addEventListener('silent-logout', handleSilentLogout);
        return () => window.removeEventListener('silent-logout', handleSilentLogout);
    }, []);

    // Start polling ONLY when user is logged in
    useEffect(() => {
        if (!user) return; // Don't poll if not logged in

        const intervalId = setInterval(async () => {
            if (!window.isLoggingOut) {
                try {
                    await api.get('/auth/user/me');
                } catch (err) {
                    // Interceptor in api.js handles 401 → shows toast and redirects
                }
            }
        }, 10000);

        return () => clearInterval(intervalId);
    }, [user]);

    const hasPermission = (permission) => {
        const currentUser = admin || user;
        if (!currentUser) return false;

        const roles = currentUser.Roles || currentUser.roles || [];
        const roleString = currentUser.Role || currentUser.role;

        if (roleString === 'Super Admin' || (Array.isArray(roles) && roles.includes('Super Admin'))) {
            return true;
        }

        const permissions = currentUser.Permissions || currentUser.permissions || [];
        return Array.isArray(permission)
            ? permission.some(p => permissions.includes(p))
            : permissions.includes(permission);
    };

    const value = {
        user,
        admin,
        loading,
        hasPermission,
        refetch: fetchSession
    };


    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
